import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import { TIMEFRAME } from 'models/EnterprisesAnalytics';
import { DEAL_STAGE } from 'models/DealRoom';
import { TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import { DEAL_STATUS_LABEL } from 'data/dealStatus';
import {
  DEAL_STATUS_FILTER_KEY,
  ENTERPRISE_BUSINESS_UNIT,
  HAS_LISTING_FILTER_KEY,
} from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/PortfolioSellerFilters/data';
import { Option } from 'models/Global';
import { isSingleElementArray } from 'utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { FilterOption } from 'models/Filters';

const useDealsFilterOptions = (units: Option[]) => {
  const createSelectOptions = useCreateSelectOptions();
  const { isOfficeManager } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const timeFilterOption = useMemo(
    () => ({
      value: TIMEFRAME_PARAM_KEYWORD,
      label: t('createdDuring'),
      shouldSortOptions: false,
      options: createSelectOptions(Object.values(TIMEFRAME)),
    }),
    [createSelectOptions, t],
  );

  const stageFilterOption = useMemo(
    () => ({
      value: DEAL_STATUS_FILTER_KEY,
      label: t('stage'),
      isMultiple: true,
      options: Object.values(DEAL_STAGE).map((value) => ({
        value,
        label: t(DEAL_STATUS_LABEL[value]),
      })),
    }),
    [t],
  );

  const businessUnitFilterOption = useMemo(
    () => ({
      value: ENTERPRISE_BUSINESS_UNIT,
      label: t('businessUnit'),
      isMultiple: true,
      options: units,
    }),
    [units, t],
  );

  const hasListingFilterOption = useMemo(
    () => ({
      value: HAS_LISTING_FILTER_KEY,
      label: t('listing'),
      options: [
        { value: 'true', label: t('withListing') },
        { value: 'false', label: t('noListing') },
      ],
    }),
    [t],
  );

  const filterOptions = useMemo(
    () =>
      [
        timeFilterOption,
        ...(!isSingleElementArray(units) && !isOfficeManager
          ? [businessUnitFilterOption]
          : []),
        stageFilterOption,
        hasListingFilterOption,
      ] as FilterOption[],
    [
      units,
      isOfficeManager,
      timeFilterOption,
      businessUnitFilterOption,
      stageFilterOption,
      hasListingFilterOption,
    ],
  );

  return {
    timeFilterOption,
    stageFilterOption,
    businessUnitFilterOption,
    hasListingFilterOption,
    filterOptions,
  };
};

export default useDealsFilterOptions;
